/**
 * gift-guard.js — DANDY Superfoods
 * ---------------------------------------------------------------------------
 * Removes orphaned free-gift line items from the cart whenever their parent
 * bundle line is removed or downgraded — on EVERY page, not just the product
 * page that added them.
 *
 * Install:
 *   1. Upload to Online Store > Themes > Edit code > Assets
 *   2. In layout/theme.liquid, inside <head>, add:
 *      <script src="{{ 'gift-guard.js' | asset_url }}" defer="defer"></script>
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* =========================================================================
   * 1. CONFIG — this is the only section you should need to edit
   * ========================================================================= */

  // The line item property that carries the bundle tier.
  // From your cart.js this is: "properties": { "Bundle": "Buy 3 Get 2 Free Gift" }
  var BUNDLE_PROPERTY = 'Bundle';

  /**
   * One rule per free gift.
   *
   *   giftVariantId    - the variant that must be removed when orphaned
   *   parentProductId  - the product that must be in the cart to justify it
   *   bundleValues     - accepted values of the Bundle property on the parent
   *                      line. Set to null to accept ANY line of that product.
   *   minParentQty     - minimum quantity on the parent line (0 = ignore)
   */
  var GIFT_RULES = [
    // NOTE: "At Home Herbalist" (52988673884433) is deliberately NOT listed.
    // The ebook ships free with all products and variants, so it has no parent
    // to be orphaned from and must never be auto-removed.
    {
      name: 'Acai Jar',
      giftVariantId: 53510737789201,
      parentProductId: 11142641418513, // Aged Odorless Garlic
      bundleValues: null,              // quantity defines the tier, not the label
      minParentQty: 3
    }
  ];

  // Shrine renders the drawer via {% section 'cart-drawer' %} in theme.liquid,
  // so the section ID is literally "cart-drawer".
  var SECTIONS_TO_RENDER = ['cart-drawer'];

  // Shrine's own <quantity-gifts> element already manages gifts on the product
  // page. Don't fight it there — only reconcile live where it's absent.
  var DEFER_TO_THEME_SELECTOR = 'quantity-gifts';

  // Let multi-step add sequences (main product, then gifts) settle first.
  var DEBOUNCE_MS = 500;

  // If section rendering fails and we're on the cart page, hard-reload so the
  // customer never sees a stale total.
  var RELOAD_FALLBACK_ON_CART_PAGE = true;

  var DEBUG = false;

  /* =========================================================================
   * 2. INTERNALS — no edits needed below
   * ========================================================================= */

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?/i;
  var busy = false;   // prevents our own writes from re-triggering us
  var queued = false; // coalesces bursts of mutations

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[gift-guard]'].concat([].slice.call(arguments)));
  }

  function getJSON(url) {
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  /** Is there a valid parent line in the cart for this rule? */
  function hasValidParent(cart, rule) {
    return cart.items.some(function (item) {
      if (item.product_id !== rule.parentProductId) return false;
      if (rule.minParentQty && item.quantity < rule.minParentQty) return false;
      if (!rule.bundleValues) return true;
      var value = item.properties ? item.properties[BUNDLE_PROPERTY] : null;
      return rule.bundleValues.indexOf(value) !== -1;
    });
  }

  /** Collect every gift line whose parent is gone or no longer qualifies. */
  function findOrphans(cart) {
    var orphans = [];
    GIFT_RULES.forEach(function (rule) {
      if (hasValidParent(cart, rule)) return;
      cart.items.forEach(function (item) {
        if (item.variant_id === rule.giftVariantId) {
          orphans.push({ key: item.key, name: rule.name });
        }
      });
    });
    return orphans;
  }

  /** Push updated section HTML into the DOM, keeping the drawer open if it was. */
  function applySections(sections) {
    if (!sections) return false;

    var oldDrawer = document.querySelector('cart-drawer');
    var wasOpen = !!(oldDrawer && oldDrawer.classList.contains('active'));

    var applied = false;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var incoming = parsed.getElementById('shopify-section-' + id) || parsed.getElementById(id);
      var target = document.getElementById('shopify-section-' + id) || document.getElementById(id);
      if (incoming && target) {
        target.innerHTML = incoming.innerHTML;
        applied = true;
      }
    });

    if (applied && wasOpen) {
      var newDrawer = document.querySelector('cart-drawer');
      if (newDrawer) newDrawer.classList.add('active');
    }

    return applied;
  }

  /** Tell the theme (and any apps) that the cart changed. */
  function broadcast(cart) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:build', { bubbles: true, detail: { cart: cart } }));
  }

  /** The main routine: read cart, drop orphans, refresh UI. */
  function reconcile() {
    if (busy) { queued = true; return Promise.resolve(); }

    return getJSON('/cart.js')
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return null;

        var orphans = findOrphans(cart);
        if (!orphans.length) { log('cart is consistent'); return null; }

        log('removing orphaned gifts:', orphans.map(function (o) { return o.name; }));

        var updates = {};
        orphans.forEach(function (o) { updates[o.key] = 0; });

        busy = true;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates, sections: SECTIONS_TO_RENDER.join(',') })
        }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        busy = false;
        if (!cart) return;

        var rendered = applySections(cart.sections);
        broadcast(cart);

        if (!rendered && RELOAD_FALLBACK_ON_CART_PAGE && /\/cart/.test(window.location.pathname)) {
          window.location.reload();
        }
      })
      .catch(function (err) {
        busy = false;
        log('error', err);
      })
      .then(function () {
        if (queued) { queued = false; return reconcile(); }
      });
  }

  /** Debounced entry point — mutations often fire in bursts. */
  var timer = null;
  function scheduleReconcile() {
    // On the product page Shrine's <quantity-gifts> adds the main product and
    // the gifts as separate requests. Reconciling mid-sequence would delete a
    // gift that is about to become valid, so let the theme own that page.
    if (document.querySelector(DEFER_TO_THEME_SELECTOR)) {
      log('deferring to theme on this page');
      return;
    }
    clearTimeout(timer);
    timer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  /* ---- Intercept fetch() ------------------------------------------------ */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : (url && url.url) || '';
    var isCartWrite = !busy && CART_MUTATION.test(href);

    return nativeFetch.apply(this, arguments).then(function (response) {
      if (isCartWrite) scheduleReconcile();
      return response;
    });
  };

  /* ---- Intercept XMLHttpRequest (covers jQuery + most apps) ------------- */
  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__giftGuardWatch = CART_MUTATION.test(String(url));
    return nativeOpen.apply(this, arguments);
  };

  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__giftGuardWatch && !busy) {
      this.addEventListener('load', scheduleReconcile);
    }
    return nativeSend.apply(this, arguments);
  };

  /* ---- Safety nets ------------------------------------------------------ */

  // Catch any inconsistency that slipped through on a previous page.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  // Back/forward cache restores a stale page with a live cart.
  window.addEventListener('pageshow', function (e) { if (e.persisted) reconcile(); });

  // Last line of defence before checkout.
  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (el) reconcile();
  }, true);

  log('active with', GIFT_RULES.length, 'rules');
})();
